import { debounced } from 'src/shared/utils/decorators/debounced';

import type { UserSettingsServiceApi } from './user-settings.api';

export type TSettingsObject<T extends object = object> = {
  id: number;
  settings: T;
};

const isSettings = <T extends object = object>(settings: T, section: string): settings is T => {
  switch (section) {
    case 'wells':
      return (
        typeof settings === 'object' &&
        settings !== null &&
        (Object.keys(settings).length === 0 ||
          'filterModel' in settings ||
          'columnsOrderModel' in settings ||
          'columnsVisibility' in settings ||
          'columnsWidth' in settings ||
          'sortingModel' in settings ||
          'pinnedColumns' in settings)
      );
    default:
      return false;
  }
};

export interface IUserSettingsService<TKey extends string = string> {
  getSettings<T extends object = object>(section: TKey, defaultSettings?: Partial<T>): Promise<T>;
  updateSettings<T extends object = object>(section: TKey, newSettings: T): Promise<void>;
}

export class UserSettingsService<TKey extends string> implements IUserSettingsService<TKey> {
  private readonly api: UserSettingsServiceApi;

  private settings = new Map<TKey, object>();

  constructor(api: UserSettingsServiceApi) {
    this.api = api;
  }

  async getSettings<T extends object = object>(section: TKey, defaultSettings?: Partial<T>): Promise<T> {
    const savedSettings = this.settings.get(section);

    if (savedSettings) {
      return savedSettings as T;
    }

    const settings = await (async () => {
      switch (section) {
        case 'wells':
          return this.api.loadWellTableSettings<T>();
        default:
          return null;
      }
    })();

    if (settings && isSettings<T>(settings, section)) {
      this.settings.set(section, settings);
      return settings;
    }

    const newData = await this.api.createWellTableSettings<T>(defaultSettings);

    if (!newData) {
      throw new Error('Failed to create settings');
    }

    this.settings.set(section, newData);

    return newData;
  }

  @debounced(500)
  async updateSettings<T extends object = object>(section: TKey, newSettings: T): Promise<void> {
    const updatedData = await (async () => {
      switch (section) {
        case 'wells':
          return this.api.updateWellTableSettings<T>(newSettings);
        default:
          return null;
      }
    })();

    if (!updatedData) {
      throw new Error('Failed to update settings');
    }

    this.settings.set(section, updatedData);
  }
}
